import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { supabase } from "../lib/supabase";

/**
 * One place that knows who is signed in and whether they're an admin.
 *
 * Previously AuthGuard, Topbar and Dashboard each ran their own
 * `getUser()` + `select from profiles` on mount — three round trips for the
 * same row on every page load, with three chances to disagree about the
 * answer. They now read it from here.
 *
 * WHAT THIS IS AND ISN'T: it decides what the panel renders. It is not a
 * security boundary — anyone can edit `isAdmin` in a debugger. Enforcement
 * lives in RLS and in the `admin_*` functions, each of which re-checks
 * `is_admin()` server-side. Treat this as routing, not as protection.
 */

type AdminIdentity = {
  id: string;
  fullName: string;
  email: string;
  isAdmin: boolean;
};

type AdminAuthValue = {
  identity: AdminIdentity | null;
  /** True until the first session + profile resolution completes. */
  checking: boolean;
  signOut: () => Promise<void>;
};

const AdminAuthContext = createContext<AdminAuthValue | null>(null);

export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const [identity, setIdentity] = useState<AdminIdentity | null>(null);
  const [checking, setChecking] = useState(true);

  const resolve = useCallback(async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session) {
      setIdentity(null);
      setChecking(false);
      return;
    }

    // `email` is deliberately not selected here. Migration 0010 revokes
    // column-level SELECT on it from the `authenticated` role, so asking for
    // it fails the whole query — and the address is already on the session,
    // which is where it should have come from anyway.
    const { data, error } = await supabase
      .from("profiles")
      .select("id, full_name, is_admin")
      .eq("id", session.user.id)
      .maybeSingle();

    // A failed read is not the same as "not an admin", but it has to be
    // treated as one — the alternative is rendering the panel to someone whose
    // permissions we could not confirm.
    if (error || !data) {
      setIdentity(null);
      setChecking(false);
      return;
    }

    setIdentity({
      id: data.id,
      fullName: data.full_name ?? "Admin",
      email: session.user.email ?? "",
      isAdmin: !!data.is_admin,
    });
    setChecking(false);
  }, []);

  useEffect(() => {
    resolve();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      // Re-resolve rather than trusting the event's session: admin status
      // lives on the profile row, not in the JWT, so a token refresh tells us
      // nothing about whether the flag is still set.
      if (event === "SIGNED_OUT") {
        setIdentity(null);
        setChecking(false);
      } else {
        resolve();
      }
    });

    return () => subscription.unsubscribe();
  }, [resolve]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setIdentity(null);
  }, []);

  const value = useMemo(
    () => ({ identity, checking, signOut }),
    [identity, checking, signOut]
  );

  return (
    <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error("useAdminAuth must be used within AdminAuthProvider");
  }
  return context;
}
